import React, { useEffect } from 'react';
import classnames from 'classnames/bind';
import { Button } from '../Button';
import { Select } from '../Select';
import CustomLink from '../CustomLink';
import { Icon } from '../Icon';
import { SupportEmailCard } from '../SupportEmailCard';
import { PhoneSupportNotice } from '../PhoneSupportNotice';
import { useSupportForm } from '../../shared/use-support-form';
import { disableScroll, enableScroll } from '../../shared/scroll';
import styles from './ContactSupportForm.css';

const cx = classnames.bind(styles);

function ContactSupportForm({ onClose, email }) {
    const { submitForm, isPending, fields, changeField, errors, isSuccessful, isFail, resetForm } = useSupportForm();

    const options = [
        'Payment or refund',
        'Problem with my account',
        'Question about features',
        'Help using the service',
        'Technical problem',
        'Something else'
    ];

    useEffect(() => {
        disableScroll();

        return () => enableScroll();
    }, []);

    const renderContent = () => {
        if (isFail) {
            return (
                <>
                    <div className={cx('noticeBlock', 'error')} role='alert'>
                        <Icon name='alertTriangle' />
                        <div>
                            <span>Message not sent</span>
                            <span>Please try again later, or contact us directly at the email address below.</span>
                        </div>
                    </div>
                    <SupportEmailCard />
                    <div className={styles.buttons}>
                        <Button
                            label='Try again'
                            className={styles.button}
                            onClick={resetForm}
                        />
                        <Button
                            label='Close'
                            className={styles.button}
                            type='secondary'
                            onClick={onClose}
                        />
                    </div>
                </>
            );
        }

        if (isSuccessful) {
            return (
                <>
                    <div className={cx('noticeBlock', 'success')} role='status'>
                        <Icon name='check' />
                        <div>
                            <span>Message sent successfully</span>
                            <span>
                                We’ll reply within 15 minutes to <br /><strong>{email}</strong>.
                            </span>
                        </div>
                    </div>
                    <Button
                        label='Close'
                        className={styles.button}
                        onClick={onClose}
                    />
                </>
            );
        }

        return (
            (
                <>
                    <p className={styles.description}>We’re here to help with any issue. Tell us what’s wrong, and we’ll take it from there.</p>
                    <p className={styles.description}>
                        Fill out the form below — or call us&nbsp;at&nbsp;<CustomLink to='tel:+14247990744'>+1&nbsp;(424)&nbsp;799-0744</CustomLink>
                    </p>
                    <PhoneSupportNotice withComingSoon />
                    <Select
                        options={options}
                        onChange={changeField}
                        value={fields.reason}
                        errorText={errors.reason}
                        name='reason'
                        label='Reason for contact (required)'
                        className={styles.selectField}
                    />
                    <div>
                        <label htmlFor='message' className={styles.label}>Your message (required):</label>
                        <textarea
                            id='message'
                            name='message'
                            className={cx('textarea', { error: Boolean(errors.message) })}
                            value={fields.message}
                            onChange={e => changeField('message', e.target.value)}
                            rows={5}
                        />
                        {Boolean(errors.message) && <p className={styles.alert}>{errors.message}.</p>}
                    </div>
                    <Button
                        label='Send'
                        buttonProps={{ type: 'submit' }}
                        className={styles.button}
                        isLoading={isPending}
                    />
                </>
            )
        );
    };

    return (
        <div className={styles.root} role='dialog' aria-label='Human Support 24/7'>
            <form
                className={styles.form}
                onSubmit={submitForm}
            >
                <div className={styles.header}>
                    <h1 className={styles.title}>
                        Human Support 24/7
                    </h1>
                    <Button
                        type='secondary'
                        iconLeft={{ name: 'close' }}
                        onClick={onClose}
                        size='small'
                    />
                </div>
                {renderContent()}
            </form>
        </div>
    );
}

export {
    ContactSupportForm
};
